/**
 * @module ui/accordion
 */
import ee from "lib/eventemitter";
import { toArray } from "utils/utils";
import logger from "utils/logger";

const debugTag = "[ACCORDION]";

// Prototype object for the accordion component

const AccordionPrototype = {
  accordion: null,

  /**
   * @method init
   * @description Adds a click event handler to the accordion element
   * @param {HTMLElement} rootElement - the root accordion element
   */
  init(rootElement) {
    if (!rootElement || !rootElement.classList.contains("c-accordion") || rootElement.classList.contains("c-accordion--skeleton")) {
      logger.info(`${debugTag} aborting because root element is invalid`);
      return;
    }
    this.accordion = rootElement;
    this.accordion.addEventListener("click", event => {
      this.onAccordionClick(event);
    });
  },

  /**
   * @method onAccordionClick
   * @description Identifies the closest button when an element inside
   * the accordion is clicked
   * @param {MouseEvent} event - a mouse event
   */
  onAccordionClick(event) {
    const el = event.target;
    if (el.classList.contains("c-button")) {
      this.onButtonClick(el);
    } else if (el.classList.contains("c-button__text")) {
      this.onButtonClick(el.parentElement);
    }
  },

  /**
   * @method onButtonClick
   * @description Hides/reveals accordion sub-lists in response to button clicks
   * @param {HTMLElement} button - the button that was clicked
   */
  onButtonClick(button) {
    const targetListID = button.getAttribute("aria-controls");
    const targetList = this.accordion.querySelector(`#${targetListID}`);
    const parentListItem = button.parentElement.parentElement;
    const parentList = parentListItem.parentElement;

    if (targetList.hasAttribute("hidden")) {
      if (parentList.classList.contains("c-accordion__list--level-0")) {
        this.hideAllSiblingLists(targetList);
      }
      this.revealList(parentListItem, targetList, button);
    } else {
      this.hideList(parentListItem, targetList, button);
    }
    ee.trigger("c-accordion:clicked", [this.accordion, button]);
  },

  /**
   * @method revealList
   * @description Reveals a specific accordion sub-list
   * @param {HTMLElement} parentListItem - the <ul> list's parent <li> container
   * @param {HTMLElement} list - the <ul> list that you want to reveal
   * @param {HTMLElement} triggerButton - the button that toggles the list's state
   */
  revealList(parentListItem, list, triggerButton) {
    parentListItem.classList.add("c-accordion__item--open");
    list.removeAttribute("hidden");
    triggerButton.setAttribute("aria-expanded", true);
  },

  /**
   * @method hideList
   * @description Hides a specific accordion sub-list
   * @param {HTMLElement} parentListItem - the <ul> list's parent <li> container
   * @param {HTMLElement} list - the <ul> list that you want to hide
   * @param {HTMLElement} triggerButton - the button that toggles the list's state
   */
  hideList(parentListItem, list, triggerButton) {
    parentListItem.classList.remove("c-accordion__item--open");
    list.setAttribute("hidden", "hidden");
    triggerButton.setAttribute("aria-expanded", false);
  },

  /**
   * @method hideAllSiblingLists
   * @description Hides all accordion lists on the same level as the one
   * provided, as well as their child lists
   * @param {HTMLElement} list - the list who's sibling lists will be hidden
   */
  hideAllSiblingLists(list) {
    const parentListItem = list.parentElement.parentElement;
    const items = toArray(parentListItem.querySelectorAll(".c-accordion__item"));
    items.forEach(item => {
      const triggerButton = item.querySelector(".c-button");
      if (!triggerButton) {
        return;
      }
      const targetListID = triggerButton.getAttribute("aria-controls");
      const targetList = item.querySelector(`#${targetListID}`);
      if (!targetList) {
        return;
      }
      this.hideList(item, targetList, triggerButton);
    });
  }
};

export default AccordionPrototype;
